/**
 * Extension Runtime — loads installed extensions and provides their features to the editor.
 */
import { apiUrl, authFetch } from '../lib/api'
import type { CompletionContext, CompletionResult } from '@codemirror/autocomplete'

interface Snippet {
  prefix: string
  body: string
  desc: string
}

interface AIPrompt {
  name: string
  prompt: string
}

let installedSnippets: Snippet[] = []
let installedPrompts: AIPrompt[] = []
let activeTheme: Record<string, string> | null = null
let loaded = false

// Built-in snippets (always available, no install needed)
const BUILTIN_SNIPPETS: Snippet[] = [
  { prefix: '!', body: '<!DOCTYPE html>\n<html lang="en">\n<head>\n  <meta charset="UTF-8">\n  <meta name="viewport" content="width=device-width, initial-scale=1.0">\n  <title>Document</title>\n</head>\n<body>\n  \n</body>\n</html>', desc: 'HTML5 boilerplate' },
  { prefix: 'html5', body: '<!DOCTYPE html>\n<html lang="en">\n<head>\n  <meta charset="UTF-8">\n  <meta name="viewport" content="width=device-width, initial-scale=1.0">\n  <title>Document</title>\n</head>\n<body>\n  \n</body>\n</html>', desc: 'HTML5 boilerplate' },
  { prefix: 'div', body: '<div>\n  \n</div>', desc: 'div element' },
  { prefix: 'a', body: '<a href=""></a>', desc: 'Anchor tag' },
  { prefix: 'img', body: '<img src="" alt="">', desc: 'Image tag' },
  { prefix: 'link', body: '<link rel="stylesheet" href="">', desc: 'CSS link' },
  { prefix: 'script', body: '<script src=""></script>', desc: 'Script tag' },
  { prefix: 'ul', body: '<ul>\n  <li></li>\n</ul>', desc: 'Unordered list' },
  { prefix: 'ol', body: '<ol>\n  <li></li>\n</ol>', desc: 'Ordered list' },
  { prefix: 'form', body: '<form action="" method="post">\n  \n</form>', desc: 'Form element' },
  { prefix: 'input', body: '<input type="" name="" id="">', desc: 'Input element' },
  { prefix: 'btn', body: '<button type="button"></button>', desc: 'Button element' },
  { prefix: 'table', body: '<table>\n  <thead>\n    <tr>\n      <th></th>\n    </tr>\n  </thead>\n  <tbody>\n    <tr>\n      <td></td>\n    </tr>\n  </tbody>\n</table>', desc: 'Table element' },
  { prefix: 'log', body: 'console.log()', desc: 'console.log' },
  { prefix: 'fn', body: 'function () {\n  \n}', desc: 'Function' },
  { prefix: 'afn', body: 'async function () {\n  \n}', desc: 'Async function' },
  { prefix: 'arr', body: '() => {\n  \n}', desc: 'Arrow function' },
  { prefix: 'imp', body: 'import {  } from \'\'', desc: 'Import statement' },
  { prefix: 'exp', body: 'export default ', desc: 'Export default' },
  { prefix: 'try', body: 'try {\n  \n} catch (e) {\n  \n}', desc: 'Try/catch block' },
  { prefix: 'if', body: 'if () {\n  \n}', desc: 'If statement' },
  { prefix: 'for', body: 'for (let i = 0; i < ; i++) {\n  \n}', desc: 'For loop' },
  { prefix: 'fore', body: 'for (const item of ) {\n  \n}', desc: 'For...of loop' },
]

export async function loadInstalledExtensions() {
  try {
    const res = await authFetch(apiUrl('/api/marketplace/installed'))
    const exts = await res.json()
    installedSnippets = []
    installedPrompts = []
    activeTheme = null

    for (const ext of exts) {
      let manifest: any = {}
      try { manifest = typeof ext.manifest === 'string' ? JSON.parse(ext.manifest) : ext.manifest } catch {}

      if (ext.type === 'snippets' && Array.isArray(manifest?.snippets)) {
        installedSnippets.push(...manifest.snippets)
      }
      if (ext.type === 'ai-prompts' && Array.isArray(manifest?.prompts)) {
        installedPrompts.push(...manifest.prompts)
      }
      if (ext.type === 'theme' && manifest?.colors) {
        // Last installed theme wins
        activeTheme = manifest.colors
      }
    }

    // Apply theme if one is installed
    applyTheme(activeTheme)
    loaded = true
  } catch {}
}

// Apply theme colors to CSS variables
function applyTheme(colors: Record<string, string> | null) {
  const root = document.documentElement
  if (!colors) return

  const mapping: Record<string, string> = {
    accent: '--accent',
    accent2: '--accent-2',
    bg0: '--bg-0',
    bg1: '--bg-1',
    bg2: '--bg-2',
    bg3: '--bg-3',
    text0: '--text-0',
    text1: '--text-1',
    text2: '--text-2',
    red: '--red',
    border: '--border',
  }

  for (const [key, cssVar] of Object.entries(mapping)) {
    if (colors[key]) root.style.setProperty(cssVar, colors[key])
  }
}

// Reset theme to defaults
export function resetTheme() {
  const root = document.documentElement
  const vars = ['--accent', '--accent-2', '--bg-0', '--bg-1', '--bg-2', '--bg-3', '--text-0', '--text-1', '--text-2', '--red', '--border']
  for (const v of vars) root.style.removeProperty(v)
  activeTheme = null
}

// Get installed AI prompts
export function getInstalledPrompts(): AIPrompt[] {
  return installedPrompts
}

// Check if a theme is active
export function hasActiveTheme(): boolean {
  return activeTheme !== null
}

// CodeMirror autocomplete source for installed snippets + built-ins
export function snippetCompletions(context: CompletionContext): CompletionResult | null {
  // Match word chars OR special chars like !
  const word = context.matchBefore(/[\w!]+/)
  if (!word || word.from === word.to) return null

  const allSnippets = [...BUILTIN_SNIPPETS, ...installedSnippets]
  const matches = allSnippets.filter(s => s.prefix.startsWith(word.text))
  if (matches.length === 0) return null

  return {
    from: word.from,
    options: matches.map(s => ({
      label: s.prefix,
      detail: s.desc,
      type: 'snippet',
      apply: s.body.replace(/\$\{?\d+[^}]*\}?/g, '').replace(/\$0/g, ''),
      boost: 10,
    })),
  }
}
